// What the map picture's two senders share (map-picture-upload in Criar mapa, map-picture-swap in Configurações):
// the checks the browser makes before sending, the words around the file, and the multipart request with its progress.
// The server decides everything again (MapPicture); these only save a round trip.

export const TYPES: Record<string, string> = { "image/webp": "WebP", "image/jpeg": "JPEG", "image/png": "PNG" };

export type Failure = "type" | "size";

export function checkPicture(file: { type: string; size: number }, maxBytes: number): Failure | null {
	if (!(file.type in TYPES)) return "type";
	return file.size > maxBytes ? "size" : null;
}

// The map's name from the file's: no extension, `-` and `_` as spaces, first letter upper. MapScenesController#name_from does the same.
export function nameFromFile(filename: string): string {
	const base = filename
		.replace(/\.[^./\\]*$/, "")
		.replace(/[-_]/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	return (base.charAt(0).toUpperCase() + base.slice(1)).slice(0, 100);
}

// The size the server stores a picture at, when it shrinks it: its long side cut to `side`, never enlarged.
export function storedSize(width: number, height: number, side: number): { width: number; height: number } | null {
	const long = Math.max(width, height);
	if (long <= side) return null;
	const scale = side / long;
	return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export function fill(template: string, values: Record<string, string>): string {
	return template.replace(/%\{(\w+)\}/g, (_, key: string) => values[key] ?? "");
}

// A megabyte is 1024², as the server's 20 MB limit is.
const MB = 1024 * 1024;

export const megabytes = (bytes: number, locale: string) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / MB);

export function fileSize(bytes: number, locale: string): string {
	if (bytes < MB) return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.max(1, Math.round(bytes / 1024)))} KB`;
	return `${megabytes(bytes, locale)} MB`;
}

type PictureBody = { error?: string; scene?: { id: string }; version?: number; ops?: unknown; mutation_id?: string };

// The body is the picture's by default; the Som panel's send form (sound_upload.ts) reuses the request with its own.
export type Answer<Body = PictureBody> = { kind: "answer"; status: number; body: Body | null } | { kind: "failed" } | { kind: "aborted" };

export type Sending<Body = PictureBody> = { done: Promise<Answer<Body>>; abort(): void };

// Sends the form as multipart so the browser reports the upload as it goes; `onSent` fires once every byte is out and the
// server is working. It resolves with the answer, a network failure or an abort, and never rejects.
export function sendPicture<Body = PictureBody>({
	url,
	method,
	form,
	csrf,
	onProgress,
	onSent,
	create = () => new XMLHttpRequest(),
}: {
	url: string;
	method: "POST" | "PATCH";
	form: FormData;
	csrf: string | undefined;
	onProgress: (loaded: number, total: number) => void;
	onSent: () => void;
	create?: () => XMLHttpRequest;
}): Sending<Body> {
	const request = create();
	const done = new Promise<Answer<Body>>((resolve) => {
		request.open(method, url);
		request.setRequestHeader("Accept", "application/json");
		if (csrf) request.setRequestHeader("X-CSRF-Token", csrf);
		request.upload.onprogress = (event) => event.lengthComputable && onProgress(event.loaded, event.total);
		request.upload.onload = () => onSent();
		request.onload = () => resolve({ kind: "answer", status: request.status, body: parse<Body>(request.responseText) });
		request.onerror = () => resolve({ kind: "failed" });
		request.ontimeout = () => resolve({ kind: "failed" });
		request.onabort = () => resolve({ kind: "aborted" });
		request.send(form);
	});
	return { done, abort: () => request.abort() };
}

function parse<Body>(text: string): Body | null {
	try {
		return JSON.parse(text) as Body;
	} catch {
		return null;
	}
}

export const csrfToken = () => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
